import { api } from '@/api/client'

/**
 * Fire-and-forget client telemetry (CLA-1018).
 *
 * The web app had no caller for `POST /telemetry/client` until the funnel
 * work: iOS batches recording/upload events through it, the SPA emitted
 * nothing, so "how far into onboarding do new owners get?" was unanswerable
 * for the surface most self-serve signups land on.
 *
 * One event per call, no batching. The events here are rare lifecycle marks
 * (a wizard step, a verification), not per-keystroke timings, so a queue
 * would be machinery without a load to carry. A failure is swallowed: the
 * server's job is to absorb cheaply and nothing user-facing may hinge on it.
 *
 * Event names are allowlisted server-side (`@allowed_events` in
 * TelemetryController); an unknown name is quarantined, not lost, but has no
 * time series — add it there first.
 */
export type ClientEvent =
  | 'onboarding_step_viewed'
  | 'onboarding_completed'
  | 'email_verification_completed'
  /**
   * The recorder never confirmed finalization within the deadline, so the
   * committed recording may be missing its tail (CLA-1432). Carries the bytes
   * and chunk count we did commit, and how long we waited.
   */
  | 'recording_finalize_unconfirmed'
  /** Audio arrived after the blob was frozen and is NOT in the note (CLA-1432). */
  | 'recording_tail_after_commit'
  /**
   * A consult capture began — the recorder is running. Shared with iOS; web
   * sends `platform: 'web'`. Exactly one `recording_stopped` follows for every
   * capture that ends, so the difference counts captures that never ended.
   */
  | 'recording_started'
  /**
   * The microphone stopped delivering mid-consult, once per loss episode
   * (CLA-1606). Shared with iOS; web sends `platform: 'web'`, a `cause`
   * (`lib/mic-loss.ts`), the tab's `visibility`, a coarse `device_class` and
   * `detected_by`. A `mic_lost` with no `mic_recovered` after it is a death.
   */
  | 'mic_lost'
  /** The same episode's microphone came back; same `cause`, gap in `duration_ms`. */
  | 'mic_recovered'
  /**
   * A consult recording stopped, by the clinician, automatically, or by a
   * discard (`stopped_reason`). Shared with iOS; web sends `platform: 'web'` in
   * its metadata. Carries the silence calibration figures for CLA-1564
   * (`max_silence_ms` and friends).
   */
  | 'recording_stopped'
  /**
   * Pending clinical insight cards rendered for a note (CLA-1928): `note_ref`,
   * `pending`, and `display` (expanded | collapsed | folded), so a card that
   * was never on screen is not mistaken for one that was turned down.
   */
  | 'insights_shown'
  /** The clinician opened collapsed or folded insights: `from`, `pending`. */
  | 'insights_expanded'
  /** A plan with proposed lines rendered for a clinician: `placement`, `proposed`. */
  | 'plan_shown'

/**
 * Row-level fields the server stores in their own columns, as opposed to
 * `metadata`. `note_id` is resolved server-side to a real note (CLA-1215),
 * which is what lets an event be joined to the note it describes.
 */
export interface ClientEventFields {
  note_id?: number | null
  /** How long the thing the event describes lasted — `mic_recovered`'s gap, as iOS sends it. */
  duration_ms?: number | null
}

export function trackClientEvent(
  event: ClientEvent,
  metadata?: Record<string, string | number | boolean>,
  fields?: ClientEventFields,
): void {
  const row: Record<string, unknown> = {
    event,
    occurred_at: new Date().toISOString(),
    metadata: metadata ?? {},
  }
  if (fields?.note_id != null) row.note_id = fields.note_id
  if (fields?.duration_ms != null) row.duration_ms = fields.duration_ms
  void api.post('/telemetry/client', { events: [row] }).catch(() => {})
}

/** The wizard step name a `/onboarding/<step>` path denotes, or null off the wizard. */
export function onboardingStepFromPath(pathname: string): string | null {
  const m = /^\/onboarding\/([a-z0-9-]+)\/?$/.exec(pathname)
  return m ? m[1] : null
}
